import { get, isEmpty } from "lodash";
import { PostHog } from "posthog-js/react";
import { useCallback, useRef } from "react";
import React from "react";
import { usePromiseTracker } from "react-promise-tracker";

import {
  getAcceptedDocumentDescriptions,
  getUploadHeader,
} from "../lib/manualDocumentUtility";
import CarrierLoader from "../steps/CarrierLoader";
import { Button } from "@shared/ui";
import { FileText, X } from "lucide-react";
import { InlineError } from "./Field";
import StepBody from "./StepBody";
import StepFooter from "./StepFooter";
import StepHeading from "./StepHeading";
import CarrierMark from "./CarrierMark";

/**
 * Combine accepted types specified in the client config into a string accepted by an <input> element for the `accept` attribute.
 * @param {*} config
 * @param {boolean} isMultiImageMode - When true, only returns image types
 * @returns
 */
const combineAcceptedTypesString = (
  config: Record<string, any>,
  isMultiImageMode: boolean = false
): string => {
  let combinedAcceptedTypesAsString = "";

  if (isMultiImageMode) {
    // In multi-image mode, only accept images
    return "image/jpeg,image/png,image/gif";
  }

  const acceptedTypes: string[] = get(
    config,
    "documentation.acceptedTypes",
    []
  );
  if (!isEmpty(acceptedTypes)) {
    combinedAcceptedTypesAsString = get(
      config,
      "documentation.acceptedTypes",
      []
    )
      .map((type: string) => {
        if (type === "pdf") return "application/pdf";
        if (type === "image") return "image/jpeg,image/png,image/gif";
        return undefined;
      })
      .filter((type: undefined | string) => type)
      .join(",");
  }

  return combinedAcceptedTypesAsString;
};

export const DocumentUpload = ({
  session,
  loginInformation,
  submitButtonHandler,
  loadingSteps,
  posthog,
  step,
  // Multi-image mode props
  isMultiImageMode = false,
  pendingDocumentKeys = [],
  pendingFileNames = [],
  onSubmitMultiImage,
  onRemoveFile,
  disableSubmit = false,
  // New prop for batch file handling
  onMultipleFilesSelected,
  // Error display prop
  validationError,
  // Optional slot for additional content (e.g. policy type selector)
  additionalContent,
  // Disable the upload button (e.g. until a required selection is made)
  disableUpload = false,
  nextStep,
}: {
  session: Record<string, any>;
  loginInformation: Record<string, any>;
  submitButtonHandler: Function;
  loadingSteps: Record<string, any>;
  posthog: PostHog;
  step: string;
  // Multi-image mode props
  isMultiImageMode?: boolean;
  pendingDocumentKeys?: string[];
  pendingFileNames?: string[];
  onSubmitMultiImage?: () => void;
  onRemoveFile?: (index: number) => void;
  disableSubmit?: boolean;
  // New prop for batch file handling
  onMultipleFilesSelected?: (
    files: { fileData: ArrayBuffer; filePath: string }[]
  ) => Promise<void>;
  // Error display prop
  validationError?: string | null;
  // Optional slot for additional content (e.g. policy type selector)
  additionalContent?: React.ReactNode;
  // Disable the upload button (e.g. until a required selection is made)
  disableUpload?: boolean;
  nextStep?: (nextStep: any, ...args: any[]) => void;
}) => {
  const { promiseInProgress } = usePromiseTracker({ area: "carrier-loader" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const carrier = get(loginInformation, "carrier.id");

  // Stable callback for multi-image submission to prevent unnecessary re-renders
  const handleSubmitMultiImage = useCallback(() => {
    // Track multi-image submission attempt
    posthog.capture("document-submit-multi-image-attempt", {
      step,
      carrier,
    });
    onSubmitMultiImage?.();
  }, [posthog, step, carrier, pendingDocumentKeys.length, onSubmitMultiImage]);

  const handleUploadOrCapture = () => {
    // Capture posthog event when user clicks upload button
    if (isMultiImageMode && pendingDocumentKeys.length > 0) {
      // User is adding an additional page in multi-image mode
      posthog.capture("document-upload-add-additional-page-click", {
        step,
        carrier,
        existingPageCount: pendingDocumentKeys.length,
      });
    } else {
      // Regular upload click
      posthog.capture("document-upload-click", {
        step,
        carrier,
      });
    }

    // Trigger .click() on the file input element which will show the file picker
    fileInputRef.current?.click();
  };

  // Handle the file(s) selected by the user
  const handleFileChange = async (event: any) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      // Process all files at once
      const filePromises = Array.from(files).map((file: any) => {
        return new Promise<{ fileData: ArrayBuffer; filePath: string } | null>(
          (resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (reader.result) {
                const fileData = reader.result as ArrayBuffer;
                const filePath = file.path || file.name;
                resolve({ fileData, filePath });
              } else {
                resolve(null);
              }
            };
            reader.readAsArrayBuffer(file);
          }
        );
      });

      // Wait for all files to be processed
      const processedFiles = await Promise.all(filePromises);

      // Filter out any failed reads
      const validFiles = processedFiles.filter(
        (file): file is { fileData: ArrayBuffer; filePath: string } =>
          file !== null
      );

      if (validFiles.length > 0) {
        if (validFiles.length === 1) {
          // Single file - use existing interface
          await submitButtonHandler(
            validFiles[0].fileData,
            validFiles[0].filePath
          );
        } else if (onMultipleFilesSelected) {
          // Multiple files - use new batch interface
          await onMultipleFilesSelected(validFiles);
        } else {
          // Fallback: process files one by one with delays
          for (let i = 0; i < validFiles.length; i++) {
            if (i > 0) {
              // Add delay to allow React state updates
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
            await submitButtonHandler(
              validFiles[i].fileData,
              validFiles[i].filePath
            );
          }
        }
      }
    }

    // Clear the input value to allow re-selecting the same files
    event.target.value = "";
  };

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader="Uploading policy document"
          loadingSteps={loadingSteps}
          processDocument={true}
        />
      ) : (
        <>
          <StepBody>
            <CarrierMark loginInformation={loginInformation} />
            <StepHeading
              title={getUploadHeader(session)}
              description={
                isMultiImageMode
                  ? "For best results, use images with clearly readable text and make sure all pages of your document are uploaded."
                  : "PDFs or clear photos where all the text is readable work best."
              }
            />

            {/* Optional slot: rendered between description and file area */}
            {additionalContent && (
              <div className="flex flex-col gap-4">
                {additionalContent}

                {/* Show file validation error if exists */}
                {validationError && (
                  <div
                    className="rounded border border-danger-border p-3"
                    role="status"
                  >
                    <InlineError>
                      <span className="block font-semibold">
                        Invalid File Combination
                      </span>
                      {validationError}
                    </InlineError>
                  </div>
                )}
              </div>
            )}

            {/* Show file validation error when no additionalContent slot is used */}
            {!additionalContent && validationError && (
              <div
                className="rounded border border-danger-border p-3"
                role="status"
              >
                <InlineError>
                  <span className="block font-semibold">
                    Invalid File Combination
                  </span>
                  {validationError}
                </InlineError>
              </div>
            )}

            {/* Show pending files list when in multi-image mode */}
            {isMultiImageMode && pendingFileNames.length > 0 && (
              <ul className="divide-y divide-subtle overflow-hidden rounded-lg border border-subtle">
                {pendingFileNames.map((fileName, index) => (
                  <li
                    key={index}
                    className="flex min-h-14 items-center gap-3 px-4 py-2"
                  >
                    <FileText
                      aria-hidden
                      strokeWidth={1.75}
                      className="size-[18px] shrink-0 text-ink-secondary"
                    />
                    <p className="min-w-0 flex-1 truncate text-body-lg text-ink">
                      {fileName.split("/").pop() || fileName}
                    </p>
                    {onRemoveFile && (
                      <button
                        onClick={() => onRemoveFile(index)}
                        className="flex size-8 shrink-0 items-center justify-center rounded text-ink-secondary transition-colors hover:bg-surface-muted hover:text-danger-ink"
                        aria-label={`Remove ${
                          fileName.split("/").pop() || fileName
                        }`}
                        type="button"
                      >
                        <X aria-hidden strokeWidth={1.75} className="size-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* Only show accepted document descriptions when NOT in multi-image mode */}
            {!isMultiImageMode && (
              <div className="rounded-lg border border-dashed border-border bg-surface-muted p-4">
                {getAcceptedDocumentDescriptions(session, nextStep)}
              </div>
            )}
            <input
              ref={fileInputRef}
              style={{ display: "none" }}
              type="file"
              aria-label="Upload policy document"
              {...(onMultipleFilesSelected ? { multiple: true } : {})}
              accept={combineAcceptedTypesString(
                get(session, "config"),
                isMultiImageMode
              )}
              onChange={handleFileChange}
            />
          </StepBody>
          <StepFooter>
            {isMultiImageMode &&
            pendingDocumentKeys.length > 0 &&
            onSubmitMultiImage ? (
              <>
                {/* Primary button: Add Additional Page in multi-image mode */}
                <Button
                  onClick={handleUploadOrCapture}
                  name="uploadOrCapture"
                  type="button"
                  disabled={disableUpload}
                >
                  Add an Additional Page
                </Button>
                {/* Secondary button: Submit Document in multi-image mode */}
                <div
                  style={{
                    opacity: disableSubmit ? 0.5 : 1,
                    pointerEvents: disableSubmit ? "none" : "auto",
                  }}
                >
                  <Button
                    variant="secondary"
                    onClick={handleSubmitMultiImage}
                    name="submitMultiImage"
                    type="button"
                  >
                    {`Submit Document (${pendingDocumentKeys.length} page${
                      pendingDocumentKeys.length !== 1 ? "s" : ""
                    })`}
                  </Button>
                </div>
              </>
            ) : (
              /* Default single upload button */
              <Button
                onClick={handleUploadOrCapture}
                name="uploadOrCapture"
                type="button"
                disabled={disableUpload}
              >
                Upload or take a photo
              </Button>
            )}
          </StepFooter>
        </>
      )}
    </>
  );
};
